import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';

// Qué hay en cámara.
//
// Se abre por variedad —«cuántos kilos de blando tengo»— porque esa es la
// pregunta que se hace cuando llama un cliente. Los lotes van debajo, y ahí lo
// que importa es el vencimiento: el queso no espera, y un lote que se pasa se
// pierde entero.
//
// La existencia NO sale de una columna guardada: se suma de los movimientos,
// igual que en el resto del programa. Una columna que alguien va sumando y
// restando se desincroniza el primer día que algo falla a mitad.

const DIAS_AVISO = 5;

function Inventario() {
  const [lotes, setLotes] = useState([]);
  // El ajuste de inventario: dar de baja queso que no se vendió.
  //
  // Hasta hoy el queso solo salía vendiéndolo. Ni porque se dañara, ni porque
  // se regalara, ni porque el conteo de la cámara no diera — y sin esa puerta,
  // los errores del inventario no tenían arreglo posible.
  const [ajustando, setAjustando] = useState(null);   // {lote, kilos, motivo}
  const [guardandoAjuste, setGuardandoAjuste] = useState(false);
  const [abierto, setAbierto] = useState({});
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(() => {
    axios.get('/api/inventario')
      .then(r => setLotes(r.data || []))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  const diasPara = (v) => v
    ? Math.floor((new Date(v + 'T00:00:00') - new Date(hoy() + 'T00:00:00')) / 86400000)
    : null;

  // El costo del kilo es del lote, no de la variedad: dos cuajadas del mismo
  // queso cuestan distinto si la leche cambió de precio entre una y otra.
  const costoKilo = (l) => (l.kilos_producidos > 0)
    ? l.costo_lote / l.kilos_producidos : 0;

  const porProducto = {};
  lotes.forEach(l => {
    if (!porProducto[l.producto_id]) {
      porProducto[l.producto_id] = {
        id: l.producto_id, nombre: l.producto, unidad: l.unidad,
        precio_mayor: l.precio_mayor, precio_detal: l.precio_detal,
        kilos: 0, piezas: 0, valor: 0, lotes: [],
      };
    }
    const g = porProducto[l.producto_id];
    g.kilos += l.kilos;
    g.piezas += l.piezas || 0;
    g.valor += l.kilos * costoKilo(l);
    g.lotes.push(l);
  });

  const variedades = Object.values(porProducto).sort((a, b) => b.kilos - a.kilos);
  const totalKilos = variedades.reduce((s, v) => s + v.kilos, 0);
  const totalValor = variedades.reduce((s, v) => s + v.valor, 0);
  const valorVenta = variedades.reduce((s, v) => s + v.kilos * (v.precio_mayor || 0), 0);

  const porVencer = lotes.filter(l => {
    const d = diasPara(l.vence);
    return d !== null && d <= DIAS_AVISO;
  }).sort((a, b) => String(a.vence).localeCompare(String(b.vence)));

  const hacerAjuste = async (e) => {
    e.preventDefault();
    const kilos = parseFloat(ajustando.kilos);
    if (!(kilos > 0)) return alert('¿Cuántos kilos?');
    setGuardandoAjuste(true);
    try {
      await axios.post('/api/inventario/ajuste', {
        cuajada_id: ajustando.lote.cuajada_id,
        kilos, fecha: ajustando.fecha, motivo: ajustando.motivo,
      });
      setAjustando(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo hacer el ajuste');
    }
    setGuardandoAjuste(false);
  };

  return (
    <>
      <div className="encabezado">
        <h1>Inventario</h1>
        <p>Cuánto queso hay, de qué variedad y en qué lotes</p>
      </div>

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--queso)' }}>
          <div className="rotulo">🧀 En cámara</div>
          <div className="valor cifra">{peso(Math.round(totalKilos * 100) / 100)}</div>
          <div className="pie">
            {variedades.length === 1 ? '1 variedad' : `${variedades.length} variedades`}
            {' · '}{lotes.length} {lotes.length === 1 ? 'lote' : 'lotes'}
          </div>
        </div>
        <div className="dato" style={{ '--color': 'var(--acento)' }}>
          <div className="rotulo">💵 Lo que costó</div>
          <div className="valor cifra">{plata(totalValor)}</div>
          <div className="pie">leche, insumos y mano de obra</div>
        </div>
        <div className="dato" style={{ '--color': 'var(--venta)' }}>
          <div className="rotulo">🏷️ Si se vendiera todo</div>
          <div className="valor cifra">{plata(valorVenta)}</div>
          <div className="pie">a precio de mayoreo</div>
        </div>
        <div className="dato" style={{ '--color': porVencer.length ? 'var(--malo)' : 'var(--bueno)' }}>
          <div className="rotulo">⏰ Por vencer</div>
          <div className="valor cifra">{porVencer.length}</div>
          <div className="pie">en {DIAS_AVISO} días o menos</div>
        </div>
      </div>

      {porVencer.length > 0 && (
        <div className="bloque">
          <h2>⏰ Sácalos primero</h2>
          <div className="cuerpo" style={{ paddingBottom: 0 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
              Estos lotes se vencen pronto. Al vender, el programa ya descuenta del
              que vence primero — pero si algo hay que empujar hoy, es esto.
            </p>
          </div>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Lote</th>
                  <th>Queso</th>
                  <th style={{ textAlign: 'right' }}>Quedan</th>
                  <th>Se vence</th>
                </tr>
              </thead>
              <tbody>
                {porVencer.map(l => {
                  const d = diasPara(l.vence);
                  return (
                    <tr key={l.cuajada_id}>
                      <td className="cifra recio">{l.lote}</td>
                      <td>{l.producto}</td>
                      <td className="num recio">{peso(l.kilos, l.unidad)}</td>
                      <td>
                        <span className={'pinta ' + (d < 0 ? 'malo' : d <= 2 ? 'malo' : 'ojo')}>
                          {d < 0 ? `vencido hace ${-d} ${-d === 1 ? 'día' : 'días'}`
                            : d === 0 ? 'hoy'
                            : `en ${d} ${d === 1 ? 'día' : 'días'}`}
                        </span>
                        <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                          {fecha(l.vence)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bloque">
        <h2>🧀 Por variedad <span className="contador">{variedades.length}</span></h2>
        {cargando ? (
          <div className="cuerpo"><p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p></div>
        ) : variedades.length === 0 ? (
          <div className="vacio">
            No hay queso en cámara. Aparece aquí solo cuando registres una cuajada
            en Producción.
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Queso</th>
                  <th style={{ textAlign: 'right' }}>Hay</th>
                  <th style={{ textAlign: 'right' }}>Costó el kilo</th>
                  <th style={{ textAlign: 'right' }}>Se vende a</th>
                  <th style={{ textAlign: 'right' }}>Deja</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {variedades.map(v => {
                  const costoK = v.kilos > 0 ? v.valor / v.kilos : 0;
                  const precio = v.precio_mayor || 0;
                  const deja = precio - costoK;
                  const pct = precio > 0 ? (deja / precio * 100) : null;
                  return (
                    <React.Fragment key={v.id}>
                      <tr>
                        <td className="recio">
                          {v.nombre}
                          <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                            {v.lotes.length} {v.lotes.length === 1 ? 'lote' : 'lotes'}
                            {v.piezas > 0 && ` · ${v.piezas} piezas`}
                          </div>
                        </td>
                        <td className="num recio">{peso(Math.round(v.kilos * 100) / 100, v.unidad)}</td>
                        <td className="num">{costoK > 0 ? plata(costoK) : '—'}</td>
                        <td className="num">
                          {precio > 0 ? plata(precio)
                            : <span className="pinta ojo">sin precio</span>}
                        </td>
                        <td className="num recio"
                            style={{ color: deja < 0 && precio > 0 ? 'var(--malo)' : undefined }}>
                          {precio > 0 && costoK > 0 ? (
                            <>
                              {plata(deja)}
                              <div style={{ fontSize: 11, fontWeight: 400 }}>
                                {pct.toFixed(0)}%
                              </div>
                            </>
                          ) : '—'}
                        </td>
                        <td>
                          <button className="boton chico suave"
                                  onClick={() => setAbierto({ ...abierto, [v.id]: !abierto[v.id] })}>
                            {abierto[v.id] ? 'Ocultar lotes' : 'Ver lotes'}
                          </button>
                        </td>
                      </tr>

                      {abierto[v.id] && v.lotes.map(l => {
                        const d = diasPara(l.vence);
                        return (
                          <tr key={'l' + l.cuajada_id} style={{ background: 'var(--raya-suave)' }}>
                            <td style={{ paddingLeft: 26, fontSize: 13 }}>
                              <span className="cifra">{l.lote}</span>
                              <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                hecho el {fecha(l.fecha)}
                              </div>
                            </td>
                            <td className="num" style={{ fontSize: 13 }}>
                              {peso(l.kilos, l.unidad)}
                            </td>
                            <td className="num" style={{ fontSize: 13 }}>
                              {costoKilo(l) > 0 ? plata(costoKilo(l)) : '—'}
                            </td>
                            <td colSpan="2" style={{ fontSize: 13 }}>
                              {l.vence ? (
                                <span className={'pinta ' + (d === null ? 'gris'
                                  : d < 0 ? 'malo' : d <= DIAS_AVISO ? 'ojo' : 'bueno')}>
                                  vence {fecha(l.vence)}
                                </span>
                              ) : '—'}
                            </td>
                            <td>
                              {/* Va en el LOTE y no en la variedad: la
                                  existencia es de un lote, con su costo y su
                                  vencimiento. Un ajuste sobre «queso
                                  campesino» no sabría de cuál sacar. */}
                              <button className="boton chico suave"
                                      title="Dar de baja kilos de este lote"
                                      onClick={() => setAjustando({
                                        lote: l, kilos: '', motivo: '',
                                        fecha: hoy() })}>
                                ✂️ Dar de baja
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {variedades.some(v => !(v.precio_mayor > 0)) && (
          <div className="cuerpo" style={{ paddingTop: 12 }}>
            <div className="aviso ojo" style={{ marginBottom: 0 }}>
              Hay variedades sin precio de mayoreo, así que no se puede calcular
              cuánto dejan. Ponles el precio en la pantalla de <strong>Quesos</strong>.
            </div>
          </div>
        )}
      </div>

      {/* ── DAR DE BAJA KILOS DE UN LOTE ──────────────────────────────────
          
          No corrige la cuajada ni toca la historia: anota un renglón nuevo
          con su fecha, su motivo y quién lo hizo. Un inventario que se puede
          corregir por debajo, sin dejar rastro, deja de servir como prueba de
          nada. */}
      {ajustando && (
        <div className="capa">
          <form className="tarjeta" onSubmit={hacerAjuste}>
            <h2>✂️ Dar de baja kilos del lote {ajustando.lote.lote}</h2>
            <div className="cuerpo">
              <p style={{ marginTop: 0, fontSize: 14 }}>
                <strong>{ajustando.lote.producto}</strong> — quedan{' '}
                <strong>{peso(ajustando.lote.kilos, ajustando.lote.unidad)}</strong>,
                hechos el {fecha(ajustando.lote.fecha)}.
              </p>
              <div className="campos">
                <label className="campo">
                  <span>¿Cuántos kilos salen?</span>
                  <input type="number" step="0.01" min="0" autoFocus
                         max={ajustando.lote.kilos}
                         value={ajustando.kilos}
                         onChange={e => setAjustando({ ...ajustando,
                           kilos: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Fecha</span>
                  <input type="date" value={ajustando.fecha}
                         onChange={e => setAjustando({ ...ajustando,
                           fecha: e.target.value })} />
                </label>
              </div>
              {/* EL MOTIVO ES OBLIGATORIO, y el servidor también lo exige.
                  Un ajuste sin motivo es un descuadre tapado: el mes que viene
                  nadie va a saber si esos kilos se dañaron, se regalaron o
                  nunca existieron — y ésa es justo la pregunta que se hace. */}
              <label className="campo">
                <span>¿Por qué? *</span>
                <input value={ajustando.motivo} required minLength={3}
                       placeholder="Se dañó · el conteo no dio · cuajada duplicada · se regaló"
                       onChange={e => setAjustando({ ...ajustando,
                         motivo: e.target.value })} />
              </label>
              <div className="aviso" style={{ marginTop: 12 }}>
                Esto <strong>no es una venta</strong>: no entra plata y no queda
                remisión. Sale de la cámara y queda anotado con la fecha, el
                motivo y su nombre, para poder explicarlo después.
              </div>
            </div>
            <div className="acciones">
              <button type="button" className="boton suave"
                      onClick={() => setAjustando(null)}>Cancelar</button>
              <button className="boton" disabled={guardandoAjuste}>
                {guardandoAjuste ? 'Guardando…' : '✂️ Dar de baja'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Inventario;
